import { MESSAGE_CHANNELS } from './enums.js';
import { evaluateCondition, fieldsUsed, validateCondition, validateEffect, FIELD_REGISTRY, } from './expression/index.js';
import { money } from './money.js';
import { apportionByValue } from './order/apportion.js';
import { compareExact, exact, formatExact, fromMinor, multiplyExact, percent, roundToMinor, subtractExact, addExact, EXACT_ZERO, } from './pricing/exact.js';
/**
 * Promotions, coupons, loyalty, referral and campaigns. §6.25 `PRM-001` to `PRM-010`, Appendix X,
 * §15.16.
 *
 * ---
 *
 * **Why this module has its own resolution loop and does not call `resolveRules`.**
 *
 * `resolveRules` (Appendix X.3) is correct for one line and one floor. A till has several lines and
 * `PRM-004` configures the floor per promotion, and four things follow that `resolveRules` cannot
 * express without being edited, which this module may not do:
 *
 * 1. It treats every rule as touching the one line, so a non-stackable winner suppresses everything.
 *    X.3 says "all lower-priority rules touching the **same line**", and an order-level promotion
 *    touches every line while a line promotion touches one.
 * 2. It takes a single request-level `marginFloor`. `PRM-004` says "a promotion **may be configured**
 *    to refuse", so the floor belongs to the promotion.
 * 3. `marginAfter(effect)` sees the effect and not the line's running value, so "after each
 *    application" (X.3 step 4) cannot be checked cumulatively when two promotions stack.
 * 4. A manager override lands only in a label string. `PRM-004` says the override "is logged", and a
 *    label is not a record: here it is a structured entry carrying who, why, which rule and the
 *    margin it permitted.
 *
 * Condition evaluation, effect validation, the field registry and the result vocabulary
 * (`SuppressionReason`) all come from `./expression`. There is one evaluator. A parity test pins
 * this loop to `resolveRules` for the cases where the two must agree.
 *
 * ---
 *
 * **Rounding.** `MR-04` allows rounding at three points. This module rounds at exactly one, the line
 * rounding of `MR-03` step 8, and at no other point. Everything between (percentages, stacked
 * promotions, apportioned shares) is exact (`./pricing/exact.js`). The step is performed here, not
 * in the chain, only because `ChainLine.promotionAmountMinor` is an integer: the line's net is
 * rounded once and the promotion amount is the price minus that rounded net, so it is the same
 * number step 8 would have produced. The mode is a caller parameter with no default, as everywhere
 * else. Loyalty points are not money and are not one of the three points; their rounding mode is
 * also a parameter.
 *
 * **Apportionment** (`MR-05` applied to promotions). An amount that belongs to several lines, an
 * order-level `amount_off` or a bundle price, is split by `apportionByValue`, the codebase's one
 * largest-remainder method, so the shares sum to the amount with no minor unit lost or invented.
 * A percentage is not apportioned: each line gets that percentage of its own value, which is
 * already proportional. Where a stacked earlier promotion has left a line's running value
 * fractional, the split is exact rational proportion instead, and the one rounding at the line
 * covers it.
 *
 * **What apportionment cannot promise, and why.** `PromotionLineResult.attributions` sums to that
 * line's `promotionMinor` exactly, always. Summing one promotion's `discountMinor` **across** lines
 * is a different question and the answer can be a minor unit short: the rounding is per line
 * (`MR-04` allows it nowhere else), each line's loss is under one unit, and the shares of whichever
 * promotions that line carries absorb it. Three lines of 1001 under a 12.5% line promotion and then
 * 100 off the order report 99 for the order promotion, not 100. `PRM-007` asks for attribution **at
 * line level** and that is what holds; a report that needs the promotion's own total should take it
 * from the promotion, not by adding up the lines.
 *
 * Both could be made exact by handing the units the lines lost back out across lines per promotion,
 * by the same largest-remainder method this already uses within a line. That is a redistribution of
 * units already rounded and not a second money rounding, so `MR-04` does not forbid it. It is not
 * done here because it would make one line's share depend on the other lines of the sale, and
 * `PRM-007` asks for the line to be truthful on its own. A decision, not a constraint.
 */
/**
 * Every code below is this module's own, not Appendix F.
 *
 * Appendix F is "blocking errors only" and its sixteen codes name nothing in §6.25: no coupon, no
 * promotion, no loyalty code exists there to reuse. `CouponRejection`'s values are the same, and so
 * is `ConsentStatus`, which Appendix E's registry has no enumeration for either. Said out loud so
 * nobody reads these as catalogue codes, following `admin.ts`.
 */
export class PromotionError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'PromotionError';
    }
}
// ---------------------------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------------------------
/**
 * Branch-local calendar dates as `YYYY-MM-DD` strings, compared as strings.
 *
 * Not through `evaluateCondition`'s ordered operators: `compareNumeric` runs `Number()` on both
 * sides, `Number('2026-01-01')` is `NaN`, and `NaN` against `NaN` compares as equal. So
 * `order.date >= '2099-01-01'` is true for every order and `<` is always false, and `between`
 * refuses string bounds altogether. A validity window that never closes is the silent margin leak
 * `PRM-003` names, so the window is its own typed check and `validatePromotion` refuses ordered
 * comparisons on date fields.
 */
function isIsoDate(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}
function requireDate(value, where) {
    if (!isIsoDate(value)) {
        throw new PromotionError('PROMOTION_BAD_DATE', `${where} must be a real YYYY-MM-DD date, got ${JSON.stringify(value)}`);
    }
    return value;
}
function addDays(date, days) {
    const d = new Date(`${date}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}
// ---------------------------------------------------------------------------------------------
// The promotion record. PRM-001, PRM-006
// ---------------------------------------------------------------------------------------------
/** `PRM-006`: "per channel (counter, storefront, marketplace)". */
export const PROMOTION_CHANNELS = ['counter', 'storefront', 'marketplace'];
/**
 * A promotion version is drafted, published, or withdrawn and kept.
 *
 * Appendix C has no promotion state machine and Appendix E no registry for one, so this is a
 * constant rather than a transcription. It lived in `packages/db` beside the check constraint that
 * reads it, and moved here when `apps/counter` needed the same three words: the counter cannot
 * import the schema package, and two lists of the same states is how a screen comes to show a
 * status the database cannot hold.
 */
export const PROMOTION_VERSION_STATUSES = ['draft', 'published', 'withdrawn'];
/** Exported so a screen can offer exactly these six and a test can prove it offers all of them. */
export const PROMOTION_EFFECT_KINDS = [
    'percent_off',
    'amount_off',
    'fixed_price',
    'free_item',
    'free_treatment',
    'upgrade',
];
function checkList(name, list) {
    if (list === undefined)
        return;
    if (list.length === 0) {
        throw new PromotionError('PROMOTION_EMPTY_SCOPE', `scope.${name} is empty. An empty list would read as everyone or as no one, so it is refused.`);
    }
    for (const value of list) {
        if (typeof value !== 'string' || value === '') {
            throw new PromotionError('PROMOTION_EMPTY_SCOPE_VALUE', `scope.${name} carries ${JSON.stringify(value)}. A blank entry is not a branch, a channel or a segment, and it would match a sale that carries no such fact at all.`);
        }
    }
}
function conditionComparisons(node, out = []) {
    if (typeof node !== 'object' || node === null)
        return out;
    const n = node;
    for (const key of ['all', 'any', 'none']) {
        const children = n[key];
        if (Array.isArray(children))
            children.forEach((c) => conditionComparisons(c, out));
    }
    if (typeof n['field'] === 'string' && typeof n['op'] === 'string') {
        out.push({ field: n['field'], op: n['op'] });
    }
    return out;
}
function isGroupPromotion(p) {
    if (p.bundle !== undefined)
        return true;
    return ((p.effect.kind === 'percent_off' || p.effect.kind === 'amount_off') &&
        p.effect.appliesTo === 'order');
}
/**
 * X.4: "Rules are validated on save against the field registry and the effect schema." Throws.
 */
export function validatePromotion(p) {
    if (p.id === '')
        throw new PromotionError('PROMOTION_NO_ID', 'A promotion needs an id');
    if (!Number.isInteger(p.priority)) {
        throw new PromotionError('PROMOTION_BAD_PRIORITY', `Priority must be an integer, got ${String(p.priority)}`);
    }
    if (!Number.isInteger(p.version) || p.version < 1) {
        throw new PromotionError('PROMOTION_BAD_VERSION', 'Version must be a positive integer (X.4)');
    }
    const start = requireDate(p.window.startsOn, `${p.id} window.startsOn`);
    const end = requireDate(p.window.endsOn, `${p.id} window.endsOn`);
    if (start > end) {
        throw new PromotionError('PROMOTION_WINDOW_INVERTED', `${p.id}: the window starts ${start} and ends ${end}, which is never open`);
    }
    validateCondition(p.condition);
    for (const { field, op } of conditionComparisons(p.condition)) {
        const type = FIELD_REGISTRY[field]?.type;
        if (type === 'date' && ['<', '<=', '>', '>=', 'between'].includes(op)) {
            throw new PromotionError('PROMOTION_DATE_COMPARISON', `${field} ${op}: ordered comparison on a date is not reliable in the rule evaluator. Use the promotion's validity window instead.`);
        }
    }
    validateEffect(p.effect);
    if (!PROMOTION_EFFECT_KINDS.includes(p.effect.kind)) {
        throw new PromotionError('PROMOTION_BAD_EFFECT', `${p.effect.kind} is not a promotion effect. PRM-001 lists percent_off, amount_off, fixed_price, free_item, free_treatment and upgrade.`);
    }
    if (p.effect.kind === 'percent_off') {
        const pct = percent(p.effect.percent);
        if (pct.n <= 0n || compareExact(pct, fromMinor(1n)) > 0) {
            throw new PromotionError('PROMOTION_BAD_PERCENT', `${p.id}: a percentage off must be above 0 and at most 100, got ${p.effect.percent}. A negative one is a surcharge and a larger one is a refund.`);
        }
    }
    if ((p.effect.kind === 'amount_off' || p.effect.kind === 'fixed_price') &&
        p.effect.amountMinor < 0n) {
        throw new PromotionError('PROMOTION_BAD_AMOUNT', `${p.id}: an amount cannot be negative`);
    }
    if (p.effect.kind === 'amount_off' && p.effect.amountMinor === 0n) {
        throw new PromotionError('PROMOTION_BAD_AMOUNT', `${p.id}: an amount off of nothing is not a promotion`);
    }
    // X.2 gives `applies_to` three values and X.3 never says what the middle one means. For an amount
    // the two readings differ in money: 1500 off a category of two lines is 1500 once or 3000 in
    // total, and which one depends on how many lines the sale happens to carry. Refused rather than
    // read as one of them.
    //
    // `percent_off` with 'category' is accepted and behaves as 'line', because a percentage of each
    // line in a category is the same number whichever name it is given, and which lines are in the
    // category is already the condition. **The remaining ambiguity is the margin floor**, not the
    // discount: read as a line promotion a breach suppresses the one line, and read as a group it
    // would suppress the whole promotion. Taken as per-line, consistently with the arithmetic. A
    // tenant that wants the all-or-nothing reading writes 'order'.
    if (p.effect.kind === 'amount_off' && p.effect.appliesTo === 'category') {
        throw new PromotionError('PROMOTION_CATEGORY_AMOUNT', `${p.id}: an amount off with appliesTo 'category' has no defined meaning. The spec does not say whether the amount comes off once across the category or off every line in it, and the two differ by the number of lines. Use 'line' for each line, or 'order' for once across the sale.`);
    }
    if (p.bundle !== undefined) {
        if (p.effect.kind !== 'fixed_price') {
            throw new PromotionError('PROMOTION_BAD_BUNDLE', `${p.id}: a bundle is priced by fixed_price, got ${p.effect.kind}`);
        }
        if (p.bundle.length < 2) {
            throw new PromotionError('PROMOTION_BAD_BUNDLE', `${p.id}: a bundle needs at least two members`);
        }
    }
    if (isGroupPromotion(p)) {
        // An order-level condition is evaluated once, against the order, so a line field would be absent
        // and the rule would silently never match.
        const lineFields = fieldsUsed(p.condition).filter((f) => f.startsWith('line.'));
        if (lineFields.length > 0) {
            throw new PromotionError('PROMOTION_ORDER_LEVEL_LINE_FIELD', `${p.id}: an order-level or bundle promotion is evaluated against the order and cannot read ${lineFields.join(', ')}.`);
        }
    }
    const s = p.scope;
    if (s) {
        checkList('branchIds', s.branchIds);
        checkList('channels', s.channels);
        checkList('segments', s.segments);
        checkList('productIds', s.productIds);
        for (const c of s.channels ?? []) {
            if (!PROMOTION_CHANNELS.includes(c)) {
                throw new PromotionError('PROMOTION_BAD_CHANNEL', `${c} is not a channel. PRM-006: ${PROMOTION_CHANNELS.join(', ')}.`);
            }
        }
    }
}
// ---------------------------------------------------------------------------------------------
// Coupons. PRM-005
// ---------------------------------------------------------------------------------------------
export const COUPON_KINDS = ['single_use', 'multi_use'];
export function validateCoupon(c) {
    if (c.code === '')
        throw new PromotionError('COUPON_NO_CODE', 'A coupon needs a code');
    if (c.startsOn !== undefined)
        requireDate(c.startsOn, `${c.code} startsOn`);
    const end = requireDate(c.expiresOn, `${c.code} expiresOn`);
    if (c.startsOn !== undefined && c.startsOn > end) {
        throw new PromotionError('COUPON_WINDOW_INVERTED', `${c.code} expires before it starts`);
    }
    for (const [name, v] of [
        ['maxRedemptions', c.maxRedemptions],
        ['perCustomerLimit', c.perCustomerLimit],
    ]) {
        if (v !== undefined && (!Number.isInteger(v) || v < 1)) {
            throw new PromotionError('COUPON_BAD_LIMIT', `${c.code} ${name} must be a positive integer, got ${String(v)}`);
        }
    }
    if (c.kind === 'single_use' &&
        (c.maxRedemptions !== undefined || c.perCustomerLimit !== undefined)) {
        throw new PromotionError('COUPON_BAD_LIMIT', `${c.code} is single use, so a redemption cap is redundant and contradictory`);
    }
}
/**
 * §15.16's failure branch: "Coupon expired or already redeemed → rejected with the specific
 * reason." Pure. A success returns the redemption record for the caller to persist, and
 * persisting it is the caller's job because this module has no I/O.
 *
 * A redemption already recorded against the same `orderId` is not counted, so re-pricing a basket
 * that was already priced does not reject its own coupon.
 */
export function checkCoupon(input) {
    const { coupon } = input;
    validateCoupon(coupon);
    requireDate(input.onDate, 'onDate');
    const reject = (code, message) => ({
        ok: false,
        code,
        message,
    });
    if (coupon.startsOn !== undefined && input.onDate < coupon.startsOn) {
        return reject('not_yet_valid', `${coupon.code} is not valid until ${coupon.startsOn}.`);
    }
    if (input.onDate > coupon.expiresOn) {
        return reject('expired', `${coupon.code} expired on ${coupon.expiresOn}.`);
    }
    if (coupon.campaignId !== undefined &&
        input.campaignId !== undefined &&
        coupon.campaignId !== input.campaignId) {
        return reject('wrong_campaign', `${coupon.code} belongs to campaign ${coupon.campaignId}, not ${input.campaignId}.`);
    }
    if (coupon.customerId !== undefined) {
        if (input.customerId === undefined) {
            return reject('customer_required', `${coupon.code} is issued to one customer, and no customer is on this sale.`);
        }
        if (input.customerId !== coupon.customerId) {
            return reject('wrong_customer', `${coupon.code} is issued to a different customer.`);
        }
    }
    const prior = input.redemptions.filter((r) => r.code === coupon.code && r.orderId !== input.orderId);
    if (coupon.kind === 'single_use' && prior.length > 0) {
        return reject('already_redeemed', `${coupon.code} has already been redeemed.`);
    }
    if (coupon.maxRedemptions !== undefined && prior.length >= coupon.maxRedemptions) {
        return reject('exhausted', `${coupon.code} has reached its limit of ${String(coupon.maxRedemptions)} redemptions.`);
    }
    if (coupon.perCustomerLimit !== undefined) {
        if (input.customerId === undefined) {
            return reject('customer_required', `${coupon.code} is limited per customer, and no customer is on this sale.`);
        }
        const mine = prior.filter((r) => r.customerId === input.customerId).length;
        if (mine >= coupon.perCustomerLimit) {
            return reject('customer_limit', `This customer has already used ${coupon.code} ${String(mine)} time(s), the limit.`);
        }
    }
    return {
        ok: true,
        redemption: {
            code: coupon.code,
            orderId: input.orderId,
            redeemedOn: input.onDate,
            ...(input.customerId !== undefined ? { customerId: input.customerId } : {}),
        },
    };
}
/** `PRM-005`'s "per-campaign" and "redemption tracking": redemptions counted by the coupon's campaign. */
export function redemptionsByCampaign(coupons, redemptions) {
    const campaignOf = new Map(coupons.map((c) => [c.code, c.campaignId]));
    const out = new Map();
    for (const r of redemptions) {
        const campaign = campaignOf.get(r.code);
        if (campaign === undefined)
            continue;
        out.set(campaign, (out.get(campaign) ?? 0) + 1);
    }
    return out;
}
function lcm(a, b) {
    let x = a;
    let y = b;
    while (y !== 0n)
        [x, y] = [y, x % y];
    return (a / x) * b;
}
function minExact(a, b) {
    return compareExact(a, b) <= 0 ? a : b;
}
/**
 * Spread `total` across the targets in proportion to their running value. Integer basis and integer
 * total go through `apportionByValue`, so the shares sum to the total exactly (`MR-05`). A fractional
 * basis, which only a stacked percentage can produce, splits as exact rational proportion.
 */
function distribute(total, targets) {
    const sum = targets.reduce((s, t) => addExact(s, t.running), EXACT_ZERO);
    if (sum.n === 0n)
        return targets.map(() => EXACT_ZERO);
    if (total.d === 1n && targets.every((t) => t.running.d === 1n)) {
        const shares = apportionByValue(total.n, targets.map((t) => ({ id: t.line.id, valueMinor: t.running.n })));
        return shares.map((s) => fromMinor(s.shareMinor));
    }
    return targets.map((t) => multiplyExact(total, exact(t.running.n * sum.d, t.running.d * sum.n)));
}
export function resolvePromotions(req) {
    money(0n, req.currency);
    const ids = new Set();
    for (const p of req.promotions) {
        validatePromotion(p);
        if (ids.has(p.id)) {
            throw new PromotionError('PROMOTION_DUPLICATE_ID', `Two promotions share the id ${p.id}`);
        }
        ids.add(p.id);
    }
    const orderDate = requireDate(req.facts['order.date'], 'facts["order.date"]');
    const lineIds = new Set();
    for (const l of req.lines) {
        if (lineIds.has(l.id)) {
            throw new PromotionError('PROMOTION_DUPLICATE_LINE', `Two lines share the id ${l.id}`);
        }
        lineIds.add(l.id);
        if (l.valueMinor < 0n || l.costMinor < 0n) {
            throw new PromotionError('PROMOTION_NEGATIVE_LINE', `Line ${l.id}: value and cost cannot be negative`);
        }
    }
    const suppressed = [];
    const applied = [];
    const overridesUsed = [];
    const note = (p, reason, explanation, lineId) => {
        suppressed.push({
            promotionId: p.id,
            ruleVersion: p.version,
            label: p.label,
            reason,
            explanation,
            ...(lineId !== undefined ? { lineId } : {}),
        });
    };
    // Coupons first: `PRM-005`, and a rejection must carry its specific reason to the till.
    //
    // Each presentation is checked against the redemption history the caller hands with it, and one
    // presentation cannot see another. So the same code twice on one sale passed both checks and was
    // spent twice, which defeats `single_use` and every cap. Refused rather than deduplicated: a till
    // that sent the same code twice has a bug the shopkeeper needs told about.
    const presentedCodes = new Set();
    for (const { coupon } of req.coupons ?? []) {
        if (presentedCodes.has(coupon.code)) {
            throw new PromotionError('PROMOTION_DUPLICATE_COUPON', `The coupon ${coupon.code} is presented twice on this sale. Each presentation is checked against its own redemption history, so the second would be spent without the first being seen.`);
        }
        presentedCodes.add(coupon.code);
    }
    const couponOutcomes = (req.coupons ?? []).map(({ coupon, redemptions }) => {
        const check = ids.has(coupon.promotionId)
            ? checkCoupon({
                coupon,
                redemptions,
                orderId: req.orderId,
                onDate: orderDate,
                ...(req.customerId !== undefined ? { customerId: req.customerId } : {}),
                ...(req.campaignId !== undefined ? { campaignId: req.campaignId } : {}),
            })
            : {
                ok: false,
                code: 'unknown_promotion',
                message: `${coupon.code} names a promotion that is not active here.`,
            };
        return { code: coupon.code, promotionId: coupon.promotionId, check };
    });
    const states = req.lines.map((line) => ({
        line,
        running: fromMinor(line.valueMinor),
        blockedBy: undefined,
        accruals: [],
        freedTreatments: new Set(),
        upgraded: false,
    }));
    /**
     * The facts a line-level condition is evaluated against.
     *
     * **`line.margin` is the margin the line entered step 7 with, not what an earlier promotion has
     * left.** This is a reading of X.3, not something X.3 states, and it wants confirming: step 1
     * collects the rules whose condition is true before step 3 applies any of them, which is taken
     * here to mean every condition on this sale sees one state, so the set of promotions that match
     * does not depend on their priority order. The consequence is worth stating plainly, because it is the one
     * that surprises: a promotion conditioned on `line.margin >= 1000` is **not** a margin guard. Put
     * a `marginFloorMinor` on it instead (`PRM-004`), which is checked after each application and
     * cumulatively.
     */
    const lineFacts = (s) => ({
        ...req.facts,
        ...(s.line.facts ?? {}),
        'line.category': s.line.category,
        ...(s.line.productId !== undefined ? { 'line.product_id': s.line.productId } : {}),
        'line.quantity': s.line.quantity,
        'line.cost': s.line.costMinor,
        'line.margin': s.line.valueMinor - s.line.costMinor,
        'line.treatment_ids': (s.line.treatments ?? []).map((t) => t.id),
    });
    /**
     * `PRM-006`. A scope dimension is satisfied only by a fact that is present and is a non-empty
     * string.
     *
     * Not `String(facts[field] ?? '')`: that turns a missing fact into a value, so a scope listing
     * `''` matched a sale that carried no branch, channel or segment at all, which is the opposite of
     * "an absent one cannot satisfy a scope that names it". `validatePromotion` now refuses a blank
     * entry on save as well; this is the half that holds for a rule that reached the database before
     * it did.
     */
    const scopeMatches = (allowed, field) => {
        const value = req.facts[field];
        if (typeof value !== 'string' || value === '')
            return false;
        return allowed.includes(value);
    };
    const inProductSet = (p, s) => {
        const set = p.scope?.productIds;
        if (set === undefined)
            return true;
        return s.line.productId !== undefined && set.includes(s.line.productId);
    };
    // X.3 step 2. A total order, so a reload never reorders two promotions at one priority.
    const ordered = [...req.promotions].sort((a, b) => b.priority - a.priority || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    /** `PRM-004`. Undefined when there is no floor or it holds; else the margin the line would be left with. */
    const floorBreach = (p, s, discount) => {
        if (p.marginFloorMinor === undefined)
            return undefined;
        const marginAfter = subtractExact(subtractExact(s.running, discount), fromMinor(s.line.costMinor));
        return compareExact(marginAfter, fromMinor(p.marginFloorMinor)) < 0 ? marginAfter : undefined;
    };
    const overrideFor = (p) => (req.overrides ?? []).find((o) => o.promotionId === p.id && o.approvedBy !== '' && o.reason !== '');
    const discountFor = (p, s) => {
        const e = p.effect;
        const none = { discount: EXACT_ZERO, gifted: 0n };
        switch (e.kind) {
            case 'percent_off':
                return { discount: multiplyExact(s.running, percent(e.percent)), gifted: 0n };
            case 'amount_off':
                return { discount: minExact(fromMinor(e.amountMinor), s.running), gifted: 0n };
            case 'fixed_price': {
                const cut = subtractExact(s.running, fromMinor(e.amountMinor));
                // A fixed price above the current price is not a promotion. It must never raise a line.
                return cut.n > 0n ? { discount: cut, gifted: 0n } : none;
            }
            case 'free_item': {
                const target = e.productId !== undefined
                    ? s.line.productId === e.productId
                    : s.line.category === e.category;
                return target && s.running.n > 0n ? { discount: s.running, gifted: s.line.costMinor } : none;
            }
            case 'free_treatment': {
                const t = (s.line.treatments ?? []).find((x) => x.id === e.treatmentId);
                if (!t || s.freedTreatments.has(t.id))
                    return none;
                return { discount: minExact(fromMinor(t.priceMinor), s.running), gifted: t.costMinor };
            }
            case 'upgrade': {
                if (s.upgraded || s.line.tier !== e.toTier)
                    return none;
                const from = s.line.tiers?.[e.fromTier];
                const to = s.line.tiers?.[e.toTier];
                if (!from || !to) {
                    throw new PromotionError('PROMOTION_UPGRADE_UNPRICED', `Line ${s.line.id}: an upgrade from ${e.fromTier} to ${e.toTier} needs both tiers priced. Guessing a price difference would silently mis-state the discount.`);
                }
                const diff = to.priceMinor - from.priceMinor;
                const costDiff = to.costMinor - from.costMinor;
                return diff > 0n
                    ? {
                        discount: minExact(fromMinor(diff), s.running),
                        gifted: costDiff > 0n ? costDiff : 0n,
                    }
                    : none;
            }
        }
    };
    const commit = (p, plan, overridden) => {
        for (const { state, discount, gifted } of plan) {
            state.accruals.push({ promotion: p, discount, gifted });
            state.running = subtractExact(state.running, discount);
            if (p.effect.kind === 'free_treatment')
                state.freedTreatments.add(p.effect.treatmentId);
            if (p.effect.kind === 'upgrade')
                state.upgraded = true;
            if (!p.stackable)
                state.blockedBy = p;
        }
        applied.push({
            promotionId: p.id,
            ruleVersion: p.version,
            label: p.label,
            effect: p.effect,
            lineIds: plan.map((x) => x.state.line.id),
            floorOverridden: overridden,
        });
    };
    const recordOverride = (p, o, s, margin) => {
        overridesUsed.push({
            ...o,
            ruleVersion: p.version,
            lineId: s.line.id,
            floorMinor: p.marginFloorMinor,
            marginAfter: formatExact(margin, 2),
        });
    };
    const blockedExplanation = (s) => `Not combined with ${s.blockedBy.label}, which cannot be stacked.`;
    for (const p of ordered) {
        if (p.disabledReason !== undefined) {
            note(p, 'disabled', `Disabled: ${p.disabledReason}`);
            continue;
        }
        if (orderDate < p.window.startsOn || orderDate > p.window.endsOn) {
            note(p, 'outside_window', `Valid ${p.window.startsOn} to ${p.window.endsOn}; this sale is dated ${orderDate}.`);
            continue;
        }
        const sc = p.scope;
        const outOf = (dimension) => note(p, 'out_of_scope', `Not offered for this ${dimension}.`);
        if (sc?.branchIds && !scopeMatches(sc.branchIds, 'order.branch_id')) {
            outOf('branch');
            continue;
        }
        if (sc?.channels && !scopeMatches(sc.channels, 'order.channel')) {
            outOf('channel');
            continue;
        }
        if (sc?.segments && !scopeMatches(sc.segments, 'customer.segment')) {
            outOf('customer segment');
            continue;
        }
        if (p.requiresCoupon === true) {
            const mine = couponOutcomes.filter((c) => c.promotionId === p.id);
            if (mine.length === 0) {
                note(p, 'coupon_required', 'Needs a coupon, and none was presented.');
                continue;
            }
            if (!mine.some((c) => c.check.ok)) {
                const first = mine[0];
                note(p, 'coupon_rejected', first.check.ok ? '' : first.check.message);
                continue;
            }
        }
        const eligible = states.filter((s) => inProductSet(p, s));
        if (eligible.length === 0) {
            outOf('product set');
            continue;
        }
        // ---- planning --------------------------------------------------------------------------
        let plan = [];
        let group = false;
        if (p.bundle !== undefined) {
            group = true;
            if (!evaluateCondition(p.condition, req.facts)) {
                note(p, 'condition_false', 'Did not apply: the conditions were not met.');
                continue;
            }
            const members = [];
            let stop = false;
            for (const m of p.bundle) {
                const candidates = eligible.filter((s) => s.line.category === m.category && !members.includes(s));
                const free = candidates.find((s) => s.blockedBy === undefined);
                if (free) {
                    members.push(free);
                }
                else {
                    const blocked = candidates[0];
                    if (blocked)
                        note(p, 'suppressed_by_non_stackable', blockedExplanation(blocked));
                    else
                        note(p, 'condition_false', `Bundle incomplete: no ${m.category} line on this sale.`);
                    stop = true;
                    break;
                }
            }
            if (stop)
                continue;
            const sum = members.reduce((s, m) => addExact(s, m.running), EXACT_ZERO);
            const price = p.effect.amountMinor;
            const cut = subtractExact(sum, fromMinor(price));
            if (cut.n <= 0n) {
                note(p, 'no_effect', 'The bundle price is not below what the items already cost.');
                continue;
            }
            const shares = distribute(cut, members);
            plan = members.map((state, i) => ({ state, discount: shares[i], gifted: 0n }));
        }
        else if (isGroupPromotion(p)) {
            group = true;
            if (!evaluateCondition(p.condition, req.facts)) {
                note(p, 'condition_false', 'Did not apply: the conditions were not met.');
                continue;
            }
            const free = eligible.filter((s) => s.blockedBy === undefined);
            if (free.length === 0) {
                note(p, 'suppressed_by_non_stackable', blockedExplanation(eligible[0]));
                continue;
            }
            const e = p.effect;
            let shares;
            if (e.kind === 'percent_off') {
                shares = free.map((s) => multiplyExact(s.running, percent(e.percent)));
            }
            else {
                const sum = free.reduce((acc, s) => addExact(acc, s.running), EXACT_ZERO);
                shares = distribute(minExact(fromMinor(e.amountMinor), sum), free);
            }
            plan = free.map((state, i) => ({ state, discount: shares[i], gifted: 0n }));
            // Lines excluded for a non-stackable winner are reported, not silently skipped (PRM-003).
            for (const s of eligible) {
                if (s.blockedBy)
                    note(p, 'suppressed_by_non_stackable', blockedExplanation(s), s.line.id);
            }
        }
        else {
            const matched = [];
            for (const s of eligible)
                if (evaluateCondition(p.condition, lineFacts(s)))
                    matched.push(s);
            if (matched.length === 0) {
                note(p, 'condition_false', 'Did not apply: the conditions were not met.');
                continue;
            }
            for (const s of matched) {
                if (s.blockedBy) {
                    note(p, 'suppressed_by_non_stackable', blockedExplanation(s), s.line.id);
                    continue;
                }
                const { discount, gifted } = discountFor(p, s);
                if (discount.n <= 0n) {
                    note(p, 'no_effect', 'Taking this off would change nothing on this line.', s.line.id);
                    continue;
                }
                plan.push({ state: s, discount, gifted });
            }
        }
        // A group promotion is all or nothing on the floor (PRM-004): a bundle price cannot be half-applied.
        if (plan.every((x) => x.discount.n <= 0n)) {
            if (group)
                note(p, 'no_effect', 'Taking this off would change nothing on this sale.');
            continue;
        }
        plan = plan.filter((x) => x.discount.n > 0n);
        const override = overrideFor(p);
        const breaches = plan
            .map((x) => ({ x, margin: floorBreach(p, x.state, x.discount) }))
            .filter((b) => b.margin !== undefined);
        let overridden = false;
        if (breaches.length > 0) {
            if (override === undefined) {
                const first = breaches[0];
                const why = `Would take the line below the margin floor ` +
                    `(${formatExact(first.margin, 2)} against a floor of ${p.marginFloorMinor.toString()}). A manager can override this.`;
                if (group) {
                    note(p, 'suppressed_by_margin_floor', why);
                    continue;
                }
                const breached = new Set(breaches.map((b) => b.x.state));
                for (const b of breaches)
                    note(p, 'suppressed_by_margin_floor', why, b.x.state.line.id);
                plan = plan.filter((x) => !breached.has(x.state));
                if (plan.length === 0)
                    continue;
            }
            else {
                overridden = true;
                for (const b of breaches)
                    recordOverride(p, override, b.x.state, b.margin);
            }
        }
        commit(p, plan, overridden);
    }
    // ---- the one rounding, MR-03 step 8 --------------------------------------------------------
    const lines = states.map((s) => {
        const total = s.accruals.reduce((acc, a) => addExact(acc, a.discount), EXACT_ZERO);
        const net = roundToMinor(subtractExact(fromMinor(s.line.valueMinor), total), req.rounding);
        const promotionMinor = s.line.valueMinor - net;
        // Split the rounded amount back to the promotions that earned it, exactly (MR-05, PRM-007).
        const scale = s.accruals.reduce((l, a) => lcm(l, a.discount.d), 1n);
        const shares = apportionByValue(promotionMinor, s.accruals.map((a, i) => ({
            id: String(i),
            valueMinor: a.discount.n * (scale / a.discount.d),
        })));
        const attributions = s.accruals.map((a, i) => ({
            promotionId: a.promotion.id,
            ruleVersion: a.promotion.version,
            label: a.promotion.label,
            discountMinor: shares[i].shareMinor,
            giftedCostMinor: a.gifted,
        }));
        return {
            lineId: s.line.id,
            promotionMinor,
            netMinor: net,
            marginMinor: net - s.line.costMinor,
            giftedCostMinor: attributions.reduce((c, a) => c + a.giftedCostMinor, 0n),
            attributions,
        };
    });
    const appliedIds = new Set(applied.map((a) => a.promotionId));
    /**
     * Which coupons were actually spent. A promotion applies once however many coupons name it, so a
     * second valid coupon for the same promotion bought nothing and was still recorded as redeemed:
     * two single-use vouchers consumed for one discount.
     *
     * The check is here, after the loop, and not where the coupons are checked, for two reasons. A
     * sale presenting one valid coupon and one expired one is an ordinary till flow, so the coupons
     * have to be checked before anything can be said about overlap. And a promotion that was never
     * going to apply, because its window is closed or it is out of scope, owes the till that reason
     * (§15.16) rather than this one, so an overlap only matters once a promotion has applied.
     *
     * **Assumption:** refused rather than resolved by taking the first. Silently spending one voucher
     * and silently discarding the other both lose a customer's money without telling anybody; the
     * cashier takes one back and keeps it for next time.
     */
    const spentOn = new Map();
    const couponRedemptions = [];
    for (const c of couponOutcomes) {
        if (!c.check.ok || !appliedIds.has(c.promotionId))
            continue;
        const already = spentOn.get(c.promotionId);
        if (already !== undefined) {
            throw new PromotionError('PROMOTION_COUPON_OVERLAP', `${already} and ${c.code} are both valid for promotion ${c.promotionId}, which can only apply once. One of them would be spent for nothing, so present one.`);
        }
        spentOn.set(c.promotionId, c.code);
        couponRedemptions.push(c.check.redemption);
    }
    return {
        lines,
        applied,
        suppressed,
        overridesUsed,
        totalPromotionMinor: lines.reduce((s, l) => s + l.promotionMinor, 0n),
        totalGiftedCostMinor: lines.reduce((s, l) => s + l.giftedCostMinor, 0n),
        coupons: couponOutcomes,
        couponRedemptions,
    };
}
/**
 * §12 edge 9: "A coating that is free above a lens tier, applied to a job that later downgrades."
 * Re-resolve the downgraded basket and ask what was lost. The caller decides whether to charge the
 * difference; this says which promotion stopped applying on which line.
 */
export function promotionsLost(before, after) {
    const now = new Set(after.lines.flatMap((l) => l.attributions.map((a) => `${a.promotionId}|${l.lineId}`)));
    return before.lines.flatMap((l) => l.attributions
        .filter((a) => !now.has(`${a.promotionId}|${l.lineId}`))
        .map((a) => ({ promotionId: a.promotionId, lineId: l.lineId })));
}
export function validateLoyaltyProgram(program) {
    money(0n, program.currency);
    if (program.earnPoints <= 0n || program.earnPerMinor <= 0n) {
        throw new PromotionError('LOYALTY_BAD_EARN', 'An earn rate needs positive points and a positive spend');
    }
    if (program.pointValueMinor <= 0n) {
        throw new PromotionError('LOYALTY_BAD_VALUE', 'A point must be worth something');
    }
    if (!Number.isInteger(program.expiryDays) || program.expiryDays < 1) {
        throw new PromotionError('LOYALTY_BAD_EXPIRY', 'expiryDays must be a positive integer');
    }
    const seen = new Set();
    for (const t of program.tiers) {
        if (t.minLifetimeMinor < 0n || t.earnMultiplierPercent < 0n) {
            throw new PromotionError('LOYALTY_BAD_TIER', `Tier ${t.id}: thresholds and multipliers cannot be negative`);
        }
        if (seen.has(t.minLifetimeMinor)) {
            throw new PromotionError('LOYALTY_BAD_TIER', `Two tiers start at ${t.minLifetimeMinor.toString()}, so which one applies is a guess`);
        }
        seen.add(t.minLifetimeMinor);
    }
    if (program.maxBurnPercentOfOrder !== undefined &&
        (program.maxBurnPercentOfOrder < 0n || program.maxBurnPercentOfOrder > 100n)) {
        throw new PromotionError('LOYALTY_BAD_BURN', 'maxBurnPercentOfOrder must be between 0 and 100');
    }
}
/** The highest tier whose threshold the customer's lifetime spend has reached. Feeds `customer.loyalty_tier`. */
export function tierFor(program, lifetimeMinor) {
    validateLoyaltyProgram(program);
    let best;
    for (const t of program.tiers) {
        if (lifetimeMinor >= t.minLifetimeMinor &&
            (best === undefined || t.minLifetimeMinor > best.minLifetimeMinor)) {
            best = t;
        }
    }
    return best;
}
export function earnPointsFor(program, spendMinor, tier) {
    validateLoyaltyProgram(program);
    if (spendMinor < 0n) {
        throw new PromotionError('LOYALTY_NEGATIVE_SPEND', 'Points are not earned on a negative spend. A refund claws points back through a burn or an adjustment.');
    }
    const multiplier = tier?.earnMultiplierPercent ?? 100n;
    return roundToMinor(exact(spendMinor * program.earnPoints * multiplier, program.earnPerMinor * 100n), program.pointsRounding);
}
function lotExpiresOn(lot, program) {
    return addDays(lot.earnedOn, program.expiryDays);
}
/** Move expired lots to zero and say what was released, so the liability movement can be ledgered. */
export function expireLots(lots, program, asOf) {
    validateLoyaltyProgram(program);
    requireDate(asOf, 'asOf');
    // A burn consumes lots by id. Two lots sharing one id were consumed once and left whole twice, so
    // burning 40 of two 30-point lots called `a` returned 40 points still standing: 20 points invented
    // against a liability `PRM-008` says is ledgered. Every entry point into the lot arithmetic
    // (`loyaltyBalance`, `loyaltyLiability`, `burnPoints`) comes through here, so the guard lives here.
    const lotIds = new Set();
    for (const l of lots) {
        if (lotIds.has(l.id)) {
            throw new PromotionError('LOYALTY_DUPLICATE_LOT', `Two loyalty lots share the id ${l.id}. A burn consumes lots by id, so a duplicate would be spent once and left whole twice.`);
        }
        lotIds.add(l.id);
    }
    const expired = [];
    const next = lots.map((l) => {
        if (l.remaining > 0n && asOf > lotExpiresOn(l, program)) {
            expired.push({ lotId: l.id, points: l.remaining });
            return { ...l, remaining: 0n };
        }
        return l;
    });
    return { lots: next, expired };
}
export function loyaltyBalance(lots, program, asOf) {
    return expireLots(lots, program, asOf).lots.reduce((s, l) => s + l.remaining, 0n);
}
/**
 * `PRM-008`'s "ledgered liability for unredeemed points", valued at `pointValueMinor` with no
 * breakage assumption: points that will never be redeemed still count, because the spec gives no
 * rate and understating a liability is the dangerous direction. The ledger has no loyalty account or
 * journal kind yet, so this returns the amount as `Money` and posts nothing.
 */
export function loyaltyLiability(lots, program, asOf) {
    return money(loyaltyBalance(lots, program, asOf) * program.pointValueMinor, program.currency);
}
/** Burn oldest-first, so a point that is about to expire is spent before one that is not. */
export function burnPoints(input) {
    const { program, points } = input;
    validateLoyaltyProgram(program);
    if (input.orderTotal.currency !== program.currency) {
        throw new PromotionError('LOYALTY_CURRENCY', `Points are valued in ${program.currency}, the sale is in ${input.orderTotal.currency}`);
    }
    if (points <= 0n)
        throw new PromotionError('LOYALTY_BAD_BURN', 'A burn is a positive number of points');
    if (program.minBurnPoints !== undefined && points < program.minBurnPoints) {
        throw new PromotionError('LOYALTY_BELOW_MINIMUM', `The minimum burn is ${program.minBurnPoints.toString()} points`);
    }
    const discountMinor = points * program.pointValueMinor;
    if (discountMinor > input.orderTotal.minor) {
        throw new PromotionError('LOYALTY_EXCEEDS_ORDER', 'Points cannot be burned for more than the sale is worth. That is a payout, not a discount.');
    }
    if (program.maxBurnPercentOfOrder !== undefined &&
        discountMinor * 100n > input.orderTotal.minor * program.maxBurnPercentOfOrder) {
        throw new PromotionError('LOYALTY_OVER_CAP', `A burn may cover at most ${program.maxBurnPercentOfOrder.toString()}% of the sale`);
    }
    const live = expireLots(input.lots, program, input.asOf).lots;
    const available = live.reduce((s, l) => s + l.remaining, 0n);
    if (points > available) {
        throw new PromotionError('LOYALTY_INSUFFICIENT', `${points.toString()} points requested, ${available.toString()} usable`);
    }
    let need = points;
    const consumed = [];
    const order = [...live].sort((a, b) => a.earnedOn < b.earnedOn ? -1 : a.earnedOn > b.earnedOn ? 1 : a.id < b.id ? -1 : 1);
    const taken = new Map();
    for (const l of order) {
        if (need === 0n)
            break;
        if (l.remaining === 0n)
            continue;
        const take = l.remaining < need ? l.remaining : need;
        taken.set(l.id, take);
        consumed.push({ lotId: l.id, points: take });
        need -= take;
    }
    return {
        lots: live.map((l) => ({ ...l, remaining: l.remaining - (taken.get(l.id) ?? 0n) })),
        consumed,
        discount: money(discountMinor, program.currency),
    };
}
/** `MR-05` for a burn: back onto the lines it paid for, by value, summing exactly. */
export function apportionBurnToLines(discount, lines) {
    return apportionByValue(discount.minor, lines);
}
export function referralReward(input) {
    if (input.scheme.rewardPoints <= 0n) {
        throw new PromotionError('REFERRAL_BAD_REWARD', 'A reward of nothing is not a scheme');
    }
    if (input.referrerId === input.referredId)
        return { issue: false, reason: 'self_referral' };
    if (input.alreadyRewarded)
        return { issue: false, reason: 'already_rewarded' };
    if (!input.referredIsNew)
        return { issue: false, reason: 'referred_not_new' };
    if (input.firstPurchaseMinor < input.scheme.minFirstPurchaseMinor) {
        return { issue: false, reason: 'below_threshold' };
    }
    return { issue: true, customerId: input.referrerId, points: input.scheme.rewardPoints };
}
/**
 * `PRM-010`. Consent is judged **when the send is planned**, not when the campaign was queued, which
 * is §12 edge 57: "Marketing consent withdrawn while a recall campaign is queued."
 *
 * **Unspecified, so parameters:** the consent model. Whether a customer who was never asked is
 * mailed is opt-in versus opt-out and a regulatory question per country, so `unknownConsent` has no
 * default. Which channels are live is `MSG-001` (email and in-app in v1), passed as
 * `enabledChannels`. A campaign that advertises a promotion that is not open on the send date is
 * refused whole: a message promising a deal the till will not honour is the dispute `PRM-003` warns of.
 */
export function planCampaignSend(input) {
    const { campaign } = input;
    requireDate(input.onDate, 'onDate');
    if (!MESSAGE_CHANNELS.includes(campaign.channel)) {
        throw new PromotionError('CAMPAIGN_BAD_CHANNEL', `${campaign.channel} is not a message channel`);
    }
    if (!input.enabledChannels.includes(campaign.channel)) {
        throw new PromotionError('CAMPAIGN_CHANNEL_UNAVAILABLE', `${campaign.channel} is not enabled for this tenant (MSG-001), so nothing can be sent.`);
    }
    for (const id of campaign.promotionIds) {
        const p = input.promotions.find((x) => x.id === id);
        if (!p) {
            throw new PromotionError('CAMPAIGN_UNKNOWN_PROMOTION', `Campaign ${campaign.id} names ${id}, which does not exist`);
        }
        if (p.disabledReason !== undefined ||
            input.onDate < p.window.startsOn ||
            input.onDate > p.window.endsOn) {
            throw new PromotionError('CAMPAIGN_PROMOTION_NOT_LIVE', `Campaign ${campaign.id} advertises ${id}, which is not open on ${input.onDate}`);
        }
    }
    const send = [];
    const suppressed = [];
    for (const r of input.recipients) {
        const status = r.consent[campaign.channel] ?? 'unknown';
        if (status === 'granted')
            send.push(r.customerId);
        else if (status === 'withdrawn')
            suppressed.push({ customerId: r.customerId, reason: 'consent_withdrawn' });
        else if (input.unknownConsent === 'send')
            send.push(r.customerId);
        else
            suppressed.push({ customerId: r.customerId, reason: 'consent_unknown' });
    }
    return { send, suppressed };
}
//# sourceMappingURL=promotions.js.map